// File System Access API types
declare global {
    interface Window {
        showDirectoryPicker(options?: {
            mode?: 'read' | 'readwrite';
        }): Promise<FileSystemDirectoryHandle>;
    }

    interface FileSystemDirectoryHandle {
        name: string;
        queryPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
        requestPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
    }
}

// 全局调试：监听所有来自父窗口的消息（仅在开发时使用）
if (typeof window !== 'undefined' && window.self !== window.top) {
    // 只在 iframe 环境中添加全局监听器
    const globalDebugListener = (event: MessageEvent) => {
        if (event.data && typeof event.data === 'object' && event.data.type === 'SHOW_DIRECTORY_PICKER_RESPONSE') {
            console.log('[全局调试] 检测到 SHOW_DIRECTORY_PICKER_RESPONSE 消息:', {
                type: event.data.type,
                requestId: event.data.requestId,
                success: event.data.success,
                hasHandle: !!event.data.handle,
                origin: event.origin,
                source: event.source
            });
        }
    };
    
    // 只在开发环境或调试模式下添加
    if (process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) {
        window.addEventListener('message', globalDebugListener);
        console.log('[SecureDirectoryPicker] 全局调试监听器已启用');
    }
}

// iframe中的安全封装
export class SecureDirectoryPicker {
    private isInIframe: boolean;

    constructor() {
        this.isInIframe = window.self !== window.top;
    }

    async show(options?: { mode?: 'read' | 'readwrite' }): Promise<FileSystemDirectoryHandle> {
        // 如果不在iframe中，直接使用
        if (!this.isInIframe) {
            if (!window.showDirectoryPicker) {
                throw new Error('File System Access API is not supported in this browser');
            }
            return await window.showDirectoryPicker(options);
        }

        // 在iframe中，先尝试直接调用（可能失败）
        if (window.showDirectoryPicker) {
            try {
                return await window.showDirectoryPicker(options);
            } catch (error) {
                // 如果是跨域错误，使用postMessage方案
                if ((error as Error).message.includes('Cross origin') || 
                    (error as Error).message.includes('sub frames')) {
                    console.warn('Direct showDirectoryPicker failed in iframe, trying postMessage approach');
                    return await this.showViaPostMessage(options);
                }
                // 其他错误直接抛出
                throw error;
            }
        }

        // 如果API不存在，尝试postMessage方案
        return await this.showViaPostMessage(options);
    }

    private async showViaPostMessage(options?: { mode?: 'read' | 'readwrite' }): Promise<FileSystemDirectoryHandle> {
        return new Promise((resolve, reject) => {
            const requestId = Date.now() + Math.random().toString(36).substr(2, 9);
            let timeoutId: NodeJS.Timeout | null = null;
            let isResolved = false;
            
            console.log('[SecureDirectoryPicker] 准备通过 postMessage 请求文件选择器, requestId:', requestId);
            
            const cleanup = () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                window.removeEventListener('message', handleMessage);
            };
            
            const safeResolve = (value: FileSystemDirectoryHandle) => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    resolve(value);
                }
            };
            
            const safeReject = (reason: any) => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    reject(reason);
                }
            };
            
            const handleMessage = (event: MessageEvent) => {
                // 记录所有收到的消息，用于调试
                console.log('[SecureDirectoryPicker] 子窗口收到任何消息:', {
                    hasData: !!event.data,
                    dataType: typeof event.data,
                    dataKeys: event.data ? Object.keys(event.data) : [],
                    type: event.data?.type,
                    requestId: event.data?.requestId,
                    expectedRequestId: requestId,
                    origin: event.origin,
                    source: event.source,
                    matches: event.data?.requestId === requestId,
                    fullData: event.data
                });
                
                // 检查是否是我们要等待的响应
                if (event.data && typeof event.data === 'object' && event.data.type === 'SHOW_DIRECTORY_PICKER_RESPONSE') {
                    console.log('[SecureDirectoryPicker] 检测到 SHOW_DIRECTORY_PICKER_RESPONSE 消息');
                    
                    // 验证 requestId 是否匹配（使用字符串比较，因为可能类型不同）
                    const receivedRequestId = String(event.data.requestId);
                    const expectedRequestId = String(requestId);
                    
                    console.log('[SecureDirectoryPicker] requestId 比较:', {
                        received: receivedRequestId,
                        expected: expectedRequestId,
                        match: receivedRequestId === expectedRequestId
                    });
                    
                    if (receivedRequestId === expectedRequestId) {
                        console.log('[SecureDirectoryPicker] 收到匹配的响应消息，开始处理');
                        
                        if (event.data.success) {
                            // 检查是否使用代理模式（handleId）或直接传递 handle
                            if (event.data.handleId) {
                                // 使用代理模式：创建代理 FileSystemDirectoryHandle
                                console.log('[SecureDirectoryPicker] 使用代理模式，handleId:', event.data.handleId);
                                const proxyHandle = this.createProxyHandle(event.data.handleId, event.data.handleName);
                                safeResolve(proxyHandle);
                            } else if (event.data.handle) {
                                // 直接传递 handle（同源场景）
                                console.log('[SecureDirectoryPicker] 直接获取 handle:', {
                                    name: event.data.handle.name,
                                    handleType: typeof event.data.handle,
                                    handleConstructor: event.data.handle.constructor?.name
                                });
                                
                                if (typeof event.data.handle === 'object' && event.data.handle.name) {
                                    safeResolve(event.data.handle);
                                } else {
                                    console.error('[SecureDirectoryPicker] handle 格式不正确:', event.data.handle);
                                    safeReject(new Error('Invalid handle received from parent window'));
                                }
                            } else {
                                console.error('[SecureDirectoryPicker] 响应成功但没有 handle 或 handleId');
                                safeReject(new Error('No handle or handleId received from parent window'));
                            }
                        } else {
                            console.error('[SecureDirectoryPicker] 响应失败:', event.data.error);
                            safeReject(new Error(event.data.error || 'Failed to show directory picker'));
                        }
                    } else {
                        console.log('[SecureDirectoryPicker] 收到响应但 requestId 不匹配，忽略', {
                            received: receivedRequestId,
                            expected: expectedRequestId
                        });
                    }
                } else {
                    console.log('[SecureDirectoryPicker] 收到其他类型的消息，忽略', {
                        hasData: !!event.data,
                        type: event.data?.type
                    });
                }
            };
            
            window.addEventListener('message', handleMessage);
            
            // 发送请求到主窗口
            const requestMessage = {
                type: 'SHOW_DIRECTORY_PICKER_REQUEST',
                requestId: requestId,
                options: options || { mode: 'readwrite' }
            };
            
            console.log('[SecureDirectoryPicker] 发送请求到父窗口:', requestMessage);
            console.log('[SecureDirectoryPicker] window.parent:', window.parent);
            
            try {
                window.parent.postMessage(requestMessage, '*');
                console.log('[SecureDirectoryPicker] 请求消息已发送');
            } catch (error) {
                console.error('[SecureDirectoryPicker] 发送消息失败:', error);
                safeReject(new Error('Failed to send message to parent window: ' + (error as Error).message));
                return;
            }
            
            // 设置超时
            timeoutId = setTimeout(() => {
                console.error('[SecureDirectoryPicker] 请求超时, requestId:', requestId);
                safeReject(new Error('Directory picker request timeout. Please ensure parent window handles SHOW_DIRECTORY_PICKER_REQUEST messages. RequestId: ' + requestId));
            }, 30000);
        });
    }

    /**
     * 创建代理 FileSystemDirectoryHandle 对象
     * 通过 postMessage 与父窗口通信来执行文件操作
     */
    private createProxyHandle(handleId: string, handleName: string): FileSystemDirectoryHandle {
        const self = this;
        
        // 创建代理对象
        const proxy = {
            name: handleName,
            kind: 'directory' as const,
            
            // 代理所有 FileSystemDirectoryHandle 的方法
            async getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FileSystemDirectoryHandle> {
                const result = await self.proxyFileOperation(handleId, 'getDirectoryHandle', { name, options });
                // 如果返回的是 handleId，创建新的代理对象
                if (result && result.handleId) {
                    return self.createProxyHandle(result.handleId, result.handleName);
                }
                return result;
            },
            
            async getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandle> {
                const result = await self.proxyFileOperation(handleId, 'getFileHandle', { name, options });
                // 如果返回的是 handleId，创建文件代理对象
                if (result && result.handleId) {
                    return self.createProxyFileHandle(result.handleId, result.handleName);
                }
                return result;
            },
            
            async removeEntry(name: string, options?: { recursive?: boolean }): Promise<void> {
                await self.proxyFileOperation(handleId, 'removeEntry', { name, options });
            },
            
            async resolve(possiblePath: string): Promise<string[] | null> {
                return await self.proxyFileOperation(handleId, 'resolve', { possiblePath });
            },
            
            async queryPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState> {
                return await self.proxyFileOperation(handleId, 'queryPermission', { descriptor });
            },
            
            async requestPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState> {
                return await self.proxyFileOperation(handleId, 'requestPermission', { descriptor });
            },
            
            async isSameEntry(other: FileSystemHandle): Promise<boolean> {
                // 简化实现：比较名称和类型
                return other.name === handleName && other.kind === 'directory';
            },
            
            // 异步迭代器支持（用于 for await...of）
            [Symbol.asyncIterator]: async function* () {
                const entries = await self.proxyFileOperation(handleId, 'entries', {});
                for (const [name, entryHandle] of entries) {
                    // 如果返回的是 handleId，创建代理对象
                    if (entryHandle && entryHandle.handleId) {
                        if (entryHandle.kind === 'directory') {
                            yield [name, self.createProxyHandle(entryHandle.handleId, entryHandle.handleName)];
                        } else {
                            // 文件类型，创建文件代理（简化处理）
                            yield [name, self.createProxyHandle(entryHandle.handleId, entryHandle.handleName) as unknown as FileSystemFileHandle];
                        }
                    } else {
                        yield [name, entryHandle];
                    }
                }
            },
            
            // entries() 方法
            async entries(): Promise<AsyncIterableIterator<[string, FileSystemFileHandle | FileSystemDirectoryHandle]>> {
                const entries = await self.proxyFileOperation(handleId, 'entries', {});
                const entryArray: Array<[string, FileSystemFileHandle | FileSystemDirectoryHandle]> = [];
                for (const [name, entryHandle] of entries) {
                    if (entryHandle && entryHandle.handleId) {
                        if (entryHandle.kind === 'directory') {
                            entryArray.push([name, self.createProxyHandle(entryHandle.handleId, entryHandle.handleName)]);
                        } else {
                            entryArray.push([name, self.createProxyHandle(entryHandle.handleId, entryHandle.handleName) as unknown as FileSystemFileHandle]);
                        }
                    } else {
                        entryArray.push([name, entryHandle]);
                    }
                }
                return (async function* () {
                    for (const entry of entryArray) {
                        yield entry;
                    }
                })();
            },
            
            // keys() 方法
            async keys(): Promise<AsyncIterableIterator<string>> {
                const entries = await self.proxyFileOperation(handleId, 'entries', {});
                return (async function* () {
                    for (const [name] of entries) {
                        yield name;
                    }
                })();
            },
            
            // values() 方法
            async values(): Promise<AsyncIterableIterator<FileSystemFileHandle | FileSystemDirectoryHandle>> {
                const entries = await self.proxyFileOperation(handleId, 'entries', {});
                return (async function* () {
                    for (const [, entryHandle] of entries) {
                        if (entryHandle && entryHandle.handleId) {
                            if (entryHandle.kind === 'directory') {
                                yield self.createProxyHandle(entryHandle.handleId, entryHandle.handleName);
                            } else {
                                yield self.createProxyHandle(entryHandle.handleId, entryHandle.handleName) as unknown as FileSystemFileHandle;
                            }
                        } else {
                            yield entryHandle;
                        }
                    }
                })();
            }
        };
        
        return proxy as unknown as FileSystemDirectoryHandle;
    }

    /**
     * 创建代理 FileSystemFileHandle 对象
     * 通过 postMessage 与父窗口通信来执行文件操作
     */
    private createProxyFileHandle(handleId: string, handleName: string): FileSystemFileHandle {
        const self = this;
        
        // 创建文件代理对象
        const proxy = {
            name: handleName,
            kind: 'file' as const,
            
            // 代理所有 FileSystemFileHandle 的方法
            async getFile(): Promise<File> {
                return await self.proxyFileOperation(handleId, 'getFile', {});
            },
            
            async createWritable(options?: { keepExistingData?: boolean }): Promise<FileSystemWritableFileStream> {
                // 创建可写流代理
                const writableId = `writable_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                return self.createProxyWritableStream(handleId, writableId, options);
            },
            
            async queryPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState> {
                return await self.proxyFileOperation(handleId, 'queryPermission', { descriptor });
            },
            
            async requestPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState> {
                return await self.proxyFileOperation(handleId, 'requestPermission', { descriptor });
            },
            
            async isSameEntry(other: FileSystemHandle): Promise<boolean> {
                // 简化实现：比较名称和类型
                return other.name === handleName && other.kind === 'file';
            }
        };
        
        return proxy as unknown as FileSystemFileHandle;
    }

    /**
     * 创建代理 FileSystemWritableFileStream 对象
     * 注意：这是一个简化实现，每次写入都通过 postMessage 完成
     * 实际应该维护流状态，但为了兼容性先这样实现
     */
    private createProxyWritableStream(fileHandleId: string, writableId: string, options?: { keepExistingData?: boolean }): FileSystemWritableFileStream {
        const self = this;
        let isClosed = false;
        let position = 0;
        let writtenData: (string | BufferSource | Blob)[] = [];
        
        const proxy = {
            async write(data: string | BufferSource | Blob): Promise<void> {
                if (isClosed) {
                    throw new Error('Stream is closed');
                }
                // 记录写入的数据和位置
                writtenData.push(data);
                // 更新位置（简化处理，假设写入所有数据）
                if (typeof data === 'string') {
                    position += new TextEncoder().encode(data).length;
                } else if (data instanceof Blob) {
                    position += data.size;
                } else {
                    position += data.byteLength;
                }
            },
            
            async seek(newPosition: number): Promise<void> {
                if (isClosed) {
                    throw new Error('Stream is closed');
                }
                position = newPosition;
            },
            
            async truncate(size: number): Promise<void> {
                if (isClosed) {
                    throw new Error('Stream is closed');
                }
                // 截断操作在 close 时执行
                await self.proxyFileOperation(fileHandleId, 'writableTruncate', {
                    writableId: writableId,
                    size: size
                });
            },
            
            async close(): Promise<void> {
                if (isClosed) {
                    return;
                }
                isClosed = true;
                
                // 将所有写入的数据一次性写入文件
                // 注意：这是简化实现，实际应该维护流状态
                try {
                    // 合并所有数据
                    let finalData: string | Blob;
                    if (writtenData.length === 1) {
                        const data = writtenData[0];
                        if (typeof data === 'string' || data instanceof Blob) {
                            finalData = data;
                        } else {
                            // BufferSource 转换为 Blob
                            finalData = new Blob([data]);
                        }
                    } else if (writtenData.every(d => typeof d === 'string')) {
                        finalData = (writtenData as string[]).join('');
                    } else {
                        // 混合类型，转换为 Blob
                        const blobs = await Promise.all(
                            writtenData.map(d => {
                                if (d instanceof Blob) return d;
                                if (typeof d === 'string') return new Blob([d]);
                                // BufferSource
                                return new Blob([d]);
                            })
                        );
                        finalData = new Blob(blobs);
                    }
                    
                    // 写入文件
                    await self.proxyFileOperation(fileHandleId, 'writableWrite', {
                        writableId: writableId,
                        data: finalData,
                        position: 0, // 从开始写入
                        options: options
                    });
                } catch (error) {
                    console.error('[ProxyWritableStream] 写入失败:', error);
                    throw error;
                }
            },
            
            get position(): number {
                return position;
            },
            
            set position(value: number) {
                position = value;
            }
        };
        
        return proxy as unknown as FileSystemWritableFileStream;
    }

    /**
     * 代理文件操作到父窗口
     */
    private async proxyFileOperation(handleId: string, operation: string, params: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const requestId = Date.now() + Math.random().toString(36).substr(2, 9);
            let timeoutId: NodeJS.Timeout | null = null;
            let isResolved = false;
            
            const cleanup = () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                window.removeEventListener('message', handleMessage);
            };
            
            const safeResolve = (value: any) => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    resolve(value);
                }
            };
            
            const safeReject = (reason: any) => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    reject(reason);
                }
            };
            
            const handleMessage = (event: MessageEvent) => {
                if (event.data && event.data.type === 'FILE_OPERATION_RESPONSE' && 
                    event.data.requestId === requestId) {
                    if (event.data.success) {
                        safeResolve(event.data.result);
                    } else {
                        safeReject(new Error(event.data.error || 'Operation failed'));
                    }
                }
            };
            
            window.addEventListener('message', handleMessage);
            
            // 发送操作请求到父窗口
            try {
                window.parent.postMessage({
                    type: 'FILE_OPERATION_REQUEST',
                    handleId: handleId,
                    operation: operation,
                    params: params,
                    requestId: requestId
                }, '*');
                
                // 设置超时
                timeoutId = setTimeout(() => {
                    safeReject(new Error(`File operation timeout: ${operation}`));
                }, 30000);
            } catch (error) {
                safeReject(new Error(`Failed to send file operation request: ${(error as Error).message}`));
            }
        });
    }
}

// 使用示例
// const picker = new SecureDirectoryPicker();
// const handle = await picker.show({ mode: 'readwrite' }).catch(console.error);

